/**
 * Supplier service with consistent API response format and full TypeScript support
 * Provides comprehensive supplier management functionality including CRUD operations,
 * pagination, search, and batch operations.
 */

import api from './apiService';
import { ApiResponse, Supplier } from '../types/apiTypes';
import { parseConsistentApiResponse } from '../utils/consistentApiParser';

class SupplierService {
  /**
   * Get all suppliers
   */
  async getAll(): Promise<Supplier[]> {
    // Silent operation

    const response = await api.get('/suppliers');
    const apiResponse = parseConsistentApiResponse<Supplier[]>(response, 'supplierService', 'getAll');
    return apiResponse.data || [];
  }

  /**
   * Get all suppliers with full API response
   */
  async getAllWithResponse(): Promise<ApiResponse<Supplier[]>> {
    if (process.env.NODE_ENV === 'development') {
      console.log('supplierService: Fetching all suppliers with full response...');
    }

    const response = await api.get('/suppliers');
    return parseConsistentApiResponse<Supplier[]>(response, 'supplierService', 'getAllWithResponse');
  }

  /**
   * Get supplier by ID
   */
  async getById(id: string): Promise<Supplier> {
    const response = await api.get(`/suppliers/${id}`);
    const apiResponse = parseConsistentApiResponse<Supplier>(response, 'supplierService', 'getById');
    return apiResponse.data!;
  }

  /**
   * Get supplier by ID with full API response
   */
  async getByIdWithResponse(id: string): Promise<ApiResponse<Supplier>> {
    const response = await api.get(`/suppliers/${id}`);
    return parseConsistentApiResponse<Supplier>(response, 'supplierService', 'getByIdWithResponse');
  }

  /**
   * Create new supplier
   */
  async create(supplierData: Partial<Supplier>): Promise<Supplier> {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('supplierService: Creating supplier with data:', supplierData);
      }

      const response = await api.post('/suppliers', supplierData);
      const apiResponse = parseConsistentApiResponse<Supplier>(response, 'supplierService', 'create');
      return apiResponse.data!;
    } catch (error) {
      console.error('supplierService: Error creating supplier');
      if (process.env.NODE_ENV === 'development') {
        console.error('supplierService: Error details:', error);
        console.error('supplierService: Error response data:', (error as any).response?.data);
        console.error('supplierService: Error status:', (error as any).response?.status);
      }
      throw error;
    }
  }

  /**
   * Create new supplier with full API response
   */
  async createWithResponse(supplierData: Partial<Supplier>): Promise<ApiResponse<Supplier>> {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('supplierService: Creating supplier with data:', supplierData);
      }

      const response = await api.post('/suppliers', supplierData);
      return parseConsistentApiResponse<Supplier>(response, 'supplierService', 'createWithResponse');
    } catch (error) {
      console.error('supplierService: Error creating supplier');
      if (process.env.NODE_ENV === 'development') {
        console.error('supplierService: Error details:', error);
        console.error('supplierService: Error response data:', (error as any).response?.data);
        console.error('supplierService: Error status:', (error as any).response?.status);
      }
      throw error;
    }
  }

  /**
   * Update supplier
   */
  async update(id: string, supplierData: Partial<Supplier>): Promise<Supplier> {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('supplierService: Updating supplier with ID:', id, 'and data:', supplierData);
      }

      const response = await api.put(`/suppliers/${id}`, supplierData);
      const apiResponse = parseConsistentApiResponse<Supplier>(response, 'supplierService', 'update');
      return apiResponse.data!;
    } catch (error) {
      console.error('supplierService: Error updating supplier');
      if (process.env.NODE_ENV === 'development') {
        console.error('supplierService: Error details:', error);
        console.error('supplierService: Error response data:', (error as any).response?.data);
      }
      throw error;
    }
  }

  /**
   * Update supplier with full API response
   */
  async updateWithResponse(id: string, supplierData: Partial<Supplier>): Promise<ApiResponse<Supplier>> {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('supplierService: Updating supplier with ID:', id, 'and data:', supplierData);
      }

      const response = await api.put(`/suppliers/${id}`, supplierData);
      return parseConsistentApiResponse<Supplier>(response, 'supplierService', 'updateWithResponse');
    } catch (error) {
      console.error('supplierService: Error updating supplier');
      if (process.env.NODE_ENV === 'development') {
        console.error('supplierService: Error details:', error);
        console.error('supplierService: Error response data:', (error as any).response?.data);
      }
      throw error;
    }
  }

  /**
   * Delete supplier
   */
  async remove(id: string): Promise<string> {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('supplierService: Deleting supplier with ID:', id);
      }

      const response = await api.delete(`/suppliers/${id}`);
      const apiResponse = parseConsistentApiResponse<{ supplier_id: string }>(response, 'supplierService', 'remove');

      // Return the ID if successful
      if (apiResponse.success) {
        return id;
      }

      return apiResponse.data?.supplier_id || id;
    } catch (error) {
      console.error('supplierService: Error deleting supplier');
      if (process.env.NODE_ENV === 'development') {
        console.error('supplierService: Error details:', error);
        console.error('supplierService: Error response data:', (error as any).response?.data);
      }
      throw error;
    }
  }

  /**
   * Delete supplier with full API response
   */
  async removeWithResponse(id: string): Promise<ApiResponse<{ supplier_id: string }>> {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('supplierService: Deleting supplier with ID:', id);
      }

      const response = await api.delete(`/suppliers/${id}`);
      return parseConsistentApiResponse<{ supplier_id: string }>(response, 'supplierService', 'removeWithResponse');
    } catch (error) {
      console.error('supplierService: Error deleting supplier');
      if (process.env.NODE_ENV === 'development') {
        console.error('supplierService: Error details:', error);
        console.error('supplierService: Error response data:', (error as any).response?.data);
      }
      throw error;
    }
  }

  /**
   * Get suppliers with pagination
   */
  async getPaginated(page: number = 1, perPage: number = 20): Promise<ApiResponse<Supplier[]>> {
    if (process.env.NODE_ENV === 'development') {
      console.log(`supplierService: Fetching suppliers page ${page} with ${perPage} items per page`);
    }

    const response = await api.get('/suppliers', {
      params: {
        page,
        per_page: perPage
      }
    });

    return parseConsistentApiResponse<Supplier[]>(response, 'supplierService', 'getPaginated');
  }

  /**
   * Search suppliers by name
   */
  async search(query: string, page: number = 1, perPage: number = 20): Promise<ApiResponse<Supplier[]>> {
    if (process.env.NODE_ENV === 'development') {
      console.log(`supplierService: Searching suppliers with query: ${query}`);
    }

    const response = await api.get('/suppliers/search', {
      params: {
        q: query,
        page,
        per_page: perPage
      }
    });

    return parseConsistentApiResponse<Supplier[]>(response, 'supplierService', 'search');
  }

  /**
   * Batch update suppliers
   */
  async batchUpdate(updates: Array<{ id: string; data: Partial<Supplier> }>): Promise<ApiResponse<Supplier[]>> {
    if (process.env.NODE_ENV === 'development') {
      console.log('supplierService: Batch updating suppliers:', updates);
    }

    const response = await api.put('/suppliers/batch', { updates });
    return parseConsistentApiResponse<Supplier[]>(response, 'supplierService', 'batchUpdate');
  }

  /**
   * Batch delete suppliers
   */
  async batchDelete(supplierIds: string[]): Promise<ApiResponse<{ deleted_count: number; deleted_ids: string[] }>> {
    if (process.env.NODE_ENV === 'development') {
      console.log('supplierService: Batch deleting suppliers:', supplierIds);
    }

    const response = await api.delete('/suppliers/batch', {
      data: { supplier_ids: supplierIds }
    });

    return parseConsistentApiResponse<{ deleted_count: number; deleted_ids: string[] }>(
      response,
      'supplierService',
      'batchDelete'
    );
  }
}

// Export singleton instance
const supplierService = new SupplierService();
export default supplierService;
